import { invariant } from '@apollo/client/utilities/globals'
import {
  Facebook,
  Add,
  ErrorOutline,
  MoreVert,
  AddCircle,
  Cancel,
} from '@mui/icons-material'
import {
  FormControl,
  Select,
  MenuItem,
  Button,
  Grid,
  Box,
  Divider,
  Typography,
  IconButton,
  DialogActions,
  DialogTitle,
  DialogContent,
  Menu,
  Tooltip,
  Checkbox,
  ListItem,
  Alert,
  SelectChangeEvent,
} from '@mui/material'
import {
  User,
  RetailerAccount,
  FacebookAccount,
  FacebookPage,
  FacebookAccountPage,
} from '@promoboxx/graphql-gateway-types'
import I18n from 'i18n-js'
import { useEffect, useState, useCallback, useMemo } from 'react'
import { makeStyles } from 'tss-react/mui'

import MenuItemWithCheckbox from '@src/lib/MenuItemWithCheckbox'

import ConfirmDialog from '../../../lib/ConfirmDialog'
import { useFacebook } from '../../../lib/useFacebook'
import useModalState from '../../../lib/useModalState'
import Loading from '../Loading'
import useTracking from '../Tracking/useTracking'

import { FacebookPermissionsExplanation } from './FacebookPermissionsExplanation'
import { FacebookPermissionsTable } from './FacebookPermissionsTable'

const useStyles = makeStyles()((theme) => {
  return {
    titleIcon: {
      color: theme.palette.facebook.main,
      marginRight: 4,
    },
    imageThumbnail: {
      maxWidth: '32px',
      height: '32px',
      borderRadius: theme.shape.borderRadius,
      overflow: 'hidden',
      backgroundColor: '#dddddd',
    },
    name: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    facebookPage: {
      border: 'solid 1px rgba(0,0,0,0.25)',
      borderRadius: theme.shape.borderRadius,
    },
    facebookPageSelected: {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.grey[100],
    },
    fixIt: {
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.secondary.contrastText,
    },
    overlay: {
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    overlayCenter: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
    },
    permissionsExplanation: {
      marginRight: 'auto',
    },
    sectionTitle: {
      marginRight: theme.spacing(1),
    },
    removeAccountIcon: {
      marginLeft: 'auto',
    },
  }
})

interface FacebookThumbnailProps {
  facebookEntity: FacebookPage | FacebookAccount
  isTokenValid: boolean
}
const FacebookThumbnail = ({
  facebookEntity,
  isTokenValid,
}: FacebookThumbnailProps) => {
  const { classes } = useStyles()
  if (isTokenValid) {
    return (
      <img
        alt={facebookEntity.name || ''}
        className={classes.imageThumbnail}
        src={facebookEntity.picture_url || ''}
      />
    )
  } else {
    return <ErrorOutline color={'error'} />
  }
}

const Overlay = () => {
  const { classes } = useStyles()
  return (
    <div className={classes.overlay}>
      <div className={classes.overlayCenter}>
        <Loading />
        <Typography color={'secondary'}>
          {I18n.t('facebookPermissionsManager.working')}
        </Typography>
      </div>
    </div>
  )
}

export interface FacebookPermissionsManageronCloseProps {
  facebookAccounts: FacebookAccount[]
  facebookPages: FacebookPage[]
}

interface FacebookPermissionsManagerProps {
  user: User
  retailerAccount: RetailerAccount
  onClose: (props?: FacebookPermissionsManageronCloseProps) => void
  initialFacebookPageId?: string | null
}
export function FacebookPermissionsManager({
  user,
  retailerAccount,
  onClose,
  initialFacebookPageId,
}: FacebookPermissionsManagerProps) {
  const { track } = useTracking()
  const { classes } = useStyles()
  const { isOpen, closeModal, openModal } = useModalState()
  const [hasAttemptedFix, setHasAttemptedFix] = useState(false)
  const {
    getLoginStatus,
    login,
    loginStatus,
    logout,
    getFacebookAccounts,
    getFacebookPages,
    getTokenFromCode,
    createFacebookAccount,
    updateFacebookAccount,
    deleteFacebookAccount,
    deleteFacebookAccountPermissions,
    createFacebookPage,
    deleteFacebookPage,
    refreshFacebookPageTokens,
    setAccountFacebookPages,
    facebookAccounts,
    facebookPages,
    isFacebookAccountTokenValid,
    isFacebookPageTokenValid,
    getManageablePageIds,
  } = useFacebook(user, retailerAccount)

  const [selectedAvailablePageIds, setSelectedAvailablePageIds] = useState<
    string[]
  >([])
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [isWorking, setIsWorking] = useState(true)
  const [availablePages, setAvailablePages] = useState<
    FacebookPage[] | FacebookAccountPage[]
  >([])
  const [isTokenValid, setIsTokenValid] = useState(false)
  const [currentFacebookAccountId, setCurrentFacebookAccountId] = useState<
    string | null
  >(null)
  const [currentPageId, setCurrentPageId] = useState(initialFacebookPageId)
  const [isIncorrectInitalPageId, setIsIncorrectInitialPageId] = useState(false)
  const [addPageMode, setAddPageMode] = useState(false)
  const [warnings, setWarnings] = useState<string[]>([])
  const showAddSelectPage = useMemo(() => {
    return (
      (!isWorking &&
        facebookPages &&
        facebookPages.length === 0 &&
        currentFacebookAccountId &&
        availablePages &&
        availablePages.length) ||
      addPageMode === true
    )
  }, [
    isWorking,
    currentFacebookAccountId,
    facebookPages,
    availablePages,
    addPageMode,
  ])
  const currentPage = useMemo(() => {
    return (
      facebookPages &&
      facebookPages.find((facebookPage) => facebookPage.id === currentPageId)
    )
  }, [currentPageId, facebookPages])
  const currentPageAccount = useMemo(() => {
    return (
      currentPage &&
      facebookAccounts &&
      // manageablePageIds.indexOf(currentPage.graph_id) !== -1
      facebookAccounts.find((facebookAccount) => {
        const manageablePageIds = getManageablePageIds([facebookAccount])
        if (
          currentPage?.graph_id &&
          manageablePageIds.indexOf(currentPage?.graph_id) !== -1
        ) {
          return facebookAccount
        } else {
          return null
        }
      })
    )
  }, [facebookAccounts, currentPage, getManageablePageIds])
  const loading = useMemo(() => {
    return facebookAccounts === null || facebookPages === null
  }, [facebookAccounts, facebookPages])
  const isValid = useMemo(() => {
    return (
      (facebookPages &&
        facebookAccounts &&
        facebookPages
          .map((facebookPage) => {
            const foundFacebookAccount = facebookAccounts.find(
              (facebookAccount) => {
                const manageablePageIds = getManageablePageIds([
                  facebookAccount,
                ])
                if (
                  facebookPage?.graph_id &&
                  manageablePageIds.indexOf(facebookPage?.graph_id) !== -1
                ) {
                  return facebookAccount
                } else {
                  return null
                }
              },
            )
            return (
              isFacebookPageTokenValid(facebookPage) &&
              foundFacebookAccount &&
              isFacebookAccountTokenValid(foundFacebookAccount)
            )
          })
          .indexOf(true) !== -1) ||
      false
    )
  }, [
    facebookAccounts,
    facebookPages,
    isFacebookAccountTokenValid,
    isFacebookPageTokenValid,
    getManageablePageIds,
  ])

  const getFacebookAccountById = useCallback(
    (facebookAccountId: string) => {
      return facebookAccounts &&
        facebookAccounts.length > 0 &&
        facebookAccountId
        ? facebookAccounts.find(
            (facebookAccount) => facebookAccount.id === facebookAccountId,
          )
        : undefined
    },
    [facebookAccounts],
  )

  const findAvailablePagesBestGuess = useCallback(() => {
    if (currentFacebookAccountId) {
      const currentFacebookAccount = getFacebookAccountById(
        currentFacebookAccountId,
      )
      if (currentFacebookAccount && currentFacebookAccount.manageable_pages) {
        let manageablePages = [...currentFacebookAccount.manageable_pages]
          .filter((manageablePage) => {
            if (facebookPages && facebookPages.length) {
              return (
                facebookPages.findIndex(
                  (facebookPage) => facebookPage.graph_id === manageablePage.id,
                ) === -1
              )
            } else {
              return true
            }
          })
          .sort((a, b) => {
            return (b.fan_count || 0) - (a.fan_count || 0)
          })

        setAvailablePages(manageablePages)
      }
    }
  }, [currentFacebookAccountId, facebookPages, getFacebookAccountById])

  const setAccountManageableFacebookPages = useCallback(
    async (facebookAccount: FacebookAccount) => {
      let pageIds: string[] = []
      const manageablePageIds = getManageablePageIds([facebookAccount])
      facebookPages.forEach((facebookPage) => {
        if (
          facebookPage.graph_id &&
          manageablePageIds.indexOf(facebookPage.graph_id) !== -1 &&
          pageIds.indexOf(facebookPage.id) === -1
        ) {
          pageIds.push(facebookPage.id)
        }
      })
      if (pageIds) {
        const params = pageIds.map((id) => ({
          id,
          accountId: facebookAccount.id,
        }))
        return setAccountFacebookPages(params)
      } //Nothing to do.
    },
    [facebookPages, setAccountFacebookPages, getManageablePageIds],
  )

  const addFacebookAccount = useCallback(() => {
    login().then(async (response: facebook.StatusResponse) => {
      if (response.status === 'connected') {
        let accessToken: string | null = null
        if (response.authResponse.accessToken) {
          accessToken = response.authResponse.accessToken
        } else if (response.authResponse.code) {
          accessToken =
            (await getTokenFromCode(response.authResponse.code)) || null
        }
        invariant(accessToken)
        setIsWorking(true)
        const facebookAccountResponse = await createFacebookAccount(accessToken)
        if (facebookAccountResponse?.data?.createFacebookAccount?.id) {
          setCurrentFacebookAccountId(
            facebookAccountResponse?.data?.createFacebookAccount?.id,
          )
        }

        //Fetch new accounts...
        const facebookAccountsResponse = await getFacebookAccounts()

        //If there currently exists Pages, get the manageable ones and refresh their tokens.
        if (
          facebookPages &&
          facebookAccountsResponse &&
          facebookAccountsResponse.data &&
          facebookAccountsResponse.data.getFacebookAccounts
        ) {
          if (facebookPages) {
            const freshFacebookAccounts =
              facebookAccountsResponse.data.getFacebookAccounts
            const freshFacebookAccount = freshFacebookAccounts.find(
              (facebookAccount) =>
                facebookAccount?.id ===
                facebookAccountResponse?.data?.createFacebookAccount?.id,
            )
            invariant(freshFacebookAccount)
            await setAccountManageableFacebookPages(freshFacebookAccount)
            await getFacebookPages()
          }
        }

        setIsWorking(false)
      } else {
        setWarnings([
          ...(warnings || []),
          I18n.t('facebookPermissionsManager.connectionError'),
        ])
        setIsWorking(false)
      }
    })
  }, [
    facebookPages,
    getTokenFromCode,
    createFacebookAccount,
    getFacebookAccounts,
    getFacebookPages,
    setAccountManageableFacebookPages,
    warnings,
    login,
  ])

  const updateFacebookAccountPermissions = useCallback(() => {
    login().then(async (response: facebook.StatusResponse) => {
      if (response.status === 'connected') {
        invariant(currentFacebookAccountId)
        const currentFacebookAccount = getFacebookAccountById(
          currentFacebookAccountId,
        )
        invariant(currentFacebookAccount)
        const oldUID = currentFacebookAccount.uid
        const newUID = response.authResponse.userID
        let accessToken: string | null = null
        if (response.authResponse.accessToken) {
          accessToken = response.authResponse.accessToken
        } else if (response.authResponse.code) {
          accessToken =
            (await getTokenFromCode(response.authResponse.code)) || null
        }
        invariant(accessToken)
        let facebookAccountId = currentFacebookAccountId

        if (oldUID === newUID) {
          //If it's the same Account, update it.
          await updateFacebookAccount(currentFacebookAccountId, accessToken)
        } else {
          //If it's a different account, create a new one
          const createdAccountId = await createFacebookAccount(accessToken)
          if (createdAccountId) {
            facebookAccountId = String(createdAccountId)
          }
        }
        if (facebookAccountId !== currentFacebookAccountId) {
          setCurrentFacebookAccountId(facebookAccountId)
        }
        const facebookAccount = getFacebookAccountById(facebookAccountId)
        invariant(facebookAccount)

        setIsTokenValid(isFacebookAccountTokenValid(facebookAccount))

        //We look at the new facebookAccount
        await setAccountManageableFacebookPages(facebookAccount)

        await Promise.all([getFacebookAccounts(), getFacebookPages()])
        setIsWorking(false)
      } else {
        setWarnings([
          ...(warnings || []),
          I18n.t('facebookPermissionsManager.connectionError'),
        ])
        setIsWorking(false)
      }
    })
  }, [
    currentFacebookAccountId,
    getTokenFromCode,
    createFacebookAccount,
    getFacebookAccountById,
    getFacebookAccounts,
    getFacebookPages,
    isFacebookAccountTokenValid,
    login,
    updateFacebookAccount,
    setAccountManageableFacebookPages,
    warnings,
  ])

  const fixFacebookAccountPermissions = useCallback(async () => {
    if (currentFacebookAccountId) {
      setIsWorking(true)
      await deleteFacebookAccountPermissions(currentFacebookAccountId)
      updateFacebookAccountPermissions()
      track({
        event: 'FacebookPermissionsManager',
        data: {
          name: 'fix facebook account permissions',
          userId: user.id,
          retailerAccountId: retailerAccount.id,
          facebookAccountId: currentFacebookAccountId,
        },
      })
    }
  }, [
    currentFacebookAccountId,
    deleteFacebookAccountPermissions,
    updateFacebookAccountPermissions,
    retailerAccount,
    user,
    track,
  ])

  const fixInstagramBusinessAccount = useCallback(async () => {
    if (currentPageId) {
      setIsWorking(true)
      await refreshFacebookPageTokens([currentPageId])
      await getFacebookPages()
      setIsWorking(false)
      track({
        event: 'FacebookPermissionsManager',
        data: {
          name: 'fix missing instagram business account',
          userId: user.id,
          retailerAccountId: retailerAccount.id,
          facebookAccountId: currentFacebookAccountId,
        },
      })
    }
  }, [
    currentFacebookAccountId,
    currentPageId,
    refreshFacebookPageTokens,
    getFacebookPages,
    retailerAccount,
    user,
    track,
  ])

  const fixFacebookPageToken = useCallback(async () => {
    if (currentPageAccount) {
      setHasAttemptedFix(true)
      setIsWorking(true)
      let ids: string[] = []
      const manageablePageIds = getManageablePageIds([currentPageAccount])
      facebookPages.forEach((facebookPage) => {
        if (
          facebookPage.graph_id &&
          manageablePageIds.indexOf(facebookPage.graph_id) !== -1
        ) {
          ids.push(facebookPage.id)
        }
      })
      await refreshFacebookPageTokens(ids)
      await getFacebookPages()
      setIsWorking(false)
      track({
        event: 'FacebookPermissionsManager',
        data: {
          name: 'fix facebook page token',
          userId: user.id,
          retailerAccountId: retailerAccount.id,
          facebookAccountId: currentFacebookAccountId,
          facebookPageId: currentPageAccount?.uid,
        },
      })
    }
  }, [
    facebookPages,
    currentPageAccount,
    currentFacebookAccountId,
    getFacebookPages,
    refreshFacebookPageTokens,
    retailerAccount,
    user,
    track,
    getManageablePageIds,
  ])

  const handleAddFacebookAccount = useCallback(async () => {
    setIsWorking(true)
    await getLoginStatus()
    if (loginStatus?.authResponse) {
      await logout()
    }
    await addFacebookAccount()
    await Promise.all([getFacebookAccounts(), getFacebookPages()])
    setIsWorking(false)
    track({
      event: 'FacebookPermissionsManager',
      data: {
        name: 'facebook account added',
        userId: user.id,
        retailerAccountId: retailerAccount.id,
      },
    })
  }, [
    loginStatus,
    getLoginStatus,
    getFacebookAccounts,
    getFacebookPages,
    user,
    retailerAccount,
    track,
    addFacebookAccount,
    logout,
  ])

  const createAndTrackFacebokPage = useCallback(
    async (uid: string, pageId: string) => {
      const newFacebookPage = await createFacebookPage(uid, pageId)
      track({
        event: 'FacebookPermissionsManager',
        data: {
          name: 'facebook page added',
          userId: user.id,
          retailerAccountId: retailerAccount.id,
        },
      })
      return newFacebookPage?.data?.createFacebookPage?.id || null
    },
    [createFacebookPage, user.id, retailerAccount.id, track],
  )

  const addFacebookPages = useCallback(async () => {
    if (
      availablePages &&
      selectedAvailablePageIds.length &&
      currentFacebookAccountId
    ) {
      setIsWorking(true)
      const currentFacebookAccount = getFacebookAccountById(
        currentFacebookAccountId,
      )
      invariant(currentFacebookAccount)
      const uid = currentFacebookAccount.uid
      invariant(uid)
      let createPagePromises: Promise<string | null>[] = []
      selectedAvailablePageIds.forEach((pageId) => {
        createPagePromises.push(createAndTrackFacebokPage(uid, pageId))
      })
      Promise.all(createPagePromises)
        .then(async (newPageIds) => {
          await getFacebookPages()
          let pagesToRefresh: string[] = []
          newPageIds.forEach((pageId) => {
            if (pageId !== null) {
              pagesToRefresh.push(pageId)
            }
          })
          await refreshFacebookPageTokens(pagesToRefresh)
          setCurrentPageId(newPageIds[0])
          setSelectedAvailablePageIds([])
          setAddPageMode(false)
          setIsWorking(false)
        })
        .catch((error) => {
          console.error(error)
          setSelectedAvailablePageIds([])
          setAddPageMode(false)
          setIsWorking(false)
        })
    }
  }, [
    createAndTrackFacebokPage,
    currentFacebookAccountId,
    availablePages,
    selectedAvailablePageIds,
    getFacebookAccountById,
    getFacebookPages,
    refreshFacebookPageTokens,
  ])

  const handleDeleteFacebookAccount = useCallback(async () => {
    if (currentFacebookAccountId) {
      setIsWorking(true)
      setAnchorEl(null)
      await deleteFacebookAccount(currentFacebookAccountId)
      await getFacebookAccounts()
      setIsWorking(false)
      track({
        event: 'FacebookPermissionsManager',
        data: {
          name: 'facebook account deleted',
          userId: user.id,
          retailerAccountId: retailerAccount.id,
          facebookAccountId: currentFacebookAccountId,
        },
      })
    }
  }, [
    currentFacebookAccountId,
    deleteFacebookAccount,
    getFacebookAccounts,
    track,
    user,
    retailerAccount,
  ])

  const handleDeleteFacebookPage = useCallback(
    async (id: string) => {
      setIsWorking(true)
      await deleteFacebookPage(id)
      await getFacebookPages()
      setIsWorking(false)
      track({
        event: 'FacebookPermissionsManager',
        data: {
          name: 'facebook account deleted',
          userId: user.id,
          retailerAccountId: retailerAccount.id,
          facebookAccountId: currentFacebookAccountId,
          facebookPageId: id,
        },
      })
    },
    [
      deleteFacebookPage,
      getFacebookPages,
      track,
      user,
      retailerAccount,
      currentFacebookAccountId,
    ],
  )

  const handleFacebookAccountChange = useCallback(
    (event: SelectChangeEvent<string | null>) => {
      setCurrentFacebookAccountId(event.target.value)
      track({
        event: 'FacebookPermissionsManager',
        data: {
          name: 'facebook account selected',
          userId: user.id,
          retailerAccountId: retailerAccount.id,
          facebookAccountId: event.target.value,
        },
      })
    },
    [setCurrentFacebookAccountId, track, user, retailerAccount],
  )

  const handleFacebookPageChange = useCallback(
    (event: SelectChangeEvent<string | null>) => {
      setCurrentPageId(event.target.value)
      track({
        event: 'FacebookPermissionsManager',
        data: {
          name: 'facebook page selected',
          userId: user.id,
          retailerAccountId: retailerAccount.id,
          facebookAccountId: currentFacebookAccountId,
          facebookPageId: event.target.value,
        },
      })
    },
    [setCurrentPageId, track, user, retailerAccount, currentFacebookAccountId],
  )

  const handleDialogCancel = useCallback(() => {
    track({
      event: 'FacebookPermissionsManager',
      data: {
        name: 'dialog closed - cancel',
        userId: user.id,
        retailerAccountId: retailerAccount.id,
      },
    })
    onClose({
      facebookAccounts: facebookAccounts || [],
      facebookPages,
    })
  }, [onClose, track, facebookAccounts, facebookPages, user, retailerAccount])

  const handleDialogDone = useCallback(() => {
    track({
      event: 'FacebookPermissionsManager',
      data: {
        name: 'dialog closed - done',
        userId: user.id,
        retailerAccountId: retailerAccount.id,
      },
    })
    onClose({
      facebookAccounts: facebookAccounts || [],
      facebookPages,
    })
  }, [facebookAccounts, facebookPages, onClose, track, user, retailerAccount])

  const handleSelectedAvailablePage = useCallback(
    (pageId: string) => {
      const index = selectedAvailablePageIds.indexOf(pageId)
      let updatedList = [...selectedAvailablePageIds]
      if (index === -1) {
        updatedList.push(pageId) //Add
      } else {
        updatedList.splice(index, 1) //Remove
      }
      setSelectedAvailablePageIds(updatedList)
    },
    [selectedAvailablePageIds, setSelectedAvailablePageIds],
  )

  //Grab facebook accounts on load...
  useEffect(() => {
    Promise.all([
      getLoginStatus(),
      getFacebookAccounts(),
      getFacebookPages(),
    ]).then(() => {
      setIsWorking(false)
    })
    track({
      event: 'FacebookPermissionsManager',
      data: {
        name: 'loaded',
        userId: user.id,
        retailerAccountId: retailerAccount.id,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  //Set the selected index of the loaded facebook accounts to 0 as a default.
  useEffect(() => {
    if (currentFacebookAccountId) {
      const foundAccount = getFacebookAccountById(currentFacebookAccountId)
      // May need to re-evaluate these to ensure they are required.
      // invariant(foundAccount)

      setIsTokenValid(isFacebookAccountTokenValid(foundAccount))
    }
  }, [
    currentFacebookAccountId,
    isFacebookAccountTokenValid,
    getFacebookAccountById,
  ])

  useEffect(() => {
    if (
      !currentFacebookAccountId &&
      facebookAccounts &&
      facebookAccounts.length &&
      facebookAccounts[0]
    ) {
      setCurrentFacebookAccountId(facebookAccounts[0].id)
    }
  }, [facebookAccounts, currentFacebookAccountId])

  useEffect(() => {
    if (facebookPages && facebookPages.length && facebookPages[0]) {
      if (
        initialFacebookPageId &&
        !facebookPages.some((facebookPage) => facebookPage.id === currentPageId)
      ) {
        setIsIncorrectInitialPageId(true)
        setCurrentPageId(facebookPages[0].id)
      } else if (
        currentPageId === null ||
        facebookPages.findIndex(
          (facebookPage) => facebookPage.id === currentPageId,
        ) === -1
      ) {
        setCurrentPageId(facebookPages[0].id)
      }
    }
    findAvailablePagesBestGuess()
  }, [
    facebookPages,
    currentPageId,
    findAvailablePagesBestGuess,
    setIsIncorrectInitialPageId,
    initialFacebookPageId,
  ])

  useEffect(() => {
    if (
      currentFacebookAccountId &&
      facebookPages &&
      facebookPages.length &&
      currentPageId === null &&
      facebookPages[0]
    ) {
      setCurrentPageId(facebookPages[0].id)
    }
  }, [currentFacebookAccountId, facebookPages, currentPageId])

  //Get pages and find manageable pages best guess.
  useEffect(() => {
    if (currentFacebookAccountId) {
      findAvailablePagesBestGuess()
    }
  }, [currentFacebookAccountId, findAvailablePagesBestGuess])

  //Used to calculate which pages the users various facebook accounts actually have access to.
  let manageablePageIds = getManageablePageIds(facebookAccounts)
  const hasUserControl =
    (currentPage?.graph_id &&
      manageablePageIds.indexOf(currentPage?.graph_id) !== -1) ||
    false

  const forceNoPagesWarning =
    (facebookPages.length === 0 || addPageMode) &&
    isTokenValid &&
    !isWorking &&
    !loading &&
    availablePages.length === 0

  return (
    <>
      <DialogTitle>
        <Box alignItems={'center'} display={'flex'}>
          <Facebook className={classes.titleIcon} fontSize={'large'} />
          <Typography variant={'h5'}>
            {I18n.t('facebookPermissionsManager.title')}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box mb={1} mt={3}>
          {loading && (
            <Box m={6}>
              <Loading />
            </Box>
          )}
          {!loading &&
            Array.isArray(facebookAccounts) &&
            facebookAccounts.length === 0 && (
              <Box
                alignItems={'center'}
                display={'flex'}
                flexDirection={'column'}
                height={'250px'}
                justifyContent={'center'}
              >
                <Facebook style={{ fontSize: '120px', color: '#dddddd' }} />
                <Button
                  color={'primary'}
                  onClick={addFacebookAccount}
                  variant={'contained'}
                >
                  <Add />
                  {I18n.t('facebookPermissionsManager.connectFacebookAccount')}
                </Button>
              </Box>
            )}
          {!loading && facebookAccounts && facebookAccounts.length > 0 && (
            <Grid container={true} spacing={4}>
              <Grid item={true} sm={4} xs={12}>
                <Box
                  alignItems={'center'}
                  display={'flex'}
                  flexDirection={'row'}
                >
                  <Typography variant={'h6'} className={classes.sectionTitle}>
                    {I18n.t('facebookPermissionsManager.profiles')}
                  </Typography>
                  <IconButton size="small" onClick={handleAddFacebookAccount}>
                    <AddCircle color="secondary" />
                  </IconButton>
                  <IconButton
                    aria-haspopup={'true'}
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                    className={classes.removeAccountIcon}
                    size="large"
                  >
                    <MoreVert />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    keepMounted={true}
                    onClose={() => setAnchorEl(null)}
                    open={Boolean(anchorEl)}
                  >
                    <MenuItem onClick={openModal}>
                      {I18n.t('facebookPermissionsManager.removeThisAccount')}
                    </MenuItem>
                  </Menu>
                </Box>
                <FormControl fullWidth={true}>
                  <Select
                    onChange={handleFacebookAccountChange}
                    value={currentFacebookAccountId}
                  >
                    {facebookAccounts.map((facebookAccount) => (
                      <MenuItemWithCheckbox
                        key={facebookAccount.id}
                        value={facebookAccount.id}
                      >
                        <Box alignItems="center" display={'flex'}>
                          <Box pr={3}>
                            <FacebookThumbnail
                              facebookEntity={facebookAccount}
                              isTokenValid={
                                facebookAccount.token_is_valid === '1'
                              }
                            />
                          </Box>
                          <Typography className={classes.name}>
                            {facebookAccount.name}
                          </Typography>
                        </Box>
                      </MenuItemWithCheckbox>
                    ))}
                  </Select>
                </FormControl>
                {isTokenValid && facebookPages && facebookPages.length > 0 && (
                  <>
                    <Box mb={3} mt={1}>
                      <Divider />
                    </Box>
                    <Box display="flex">
                      <Typography
                        variant={'h6'}
                        className={classes.sectionTitle}
                      >
                        {I18n.t('facebookPermissionsManager.pages')}
                      </Typography>
                      <IconButton
                        size="small"
                        disabled={
                          !isTokenValid ||
                          (facebookPages && facebookPages.length === 0)
                        }
                        onClick={() => setAddPageMode(!addPageMode)}
                      >
                        {addPageMode ? (
                          <Cancel color="error" />
                        ) : (
                          <AddCircle color="secondary" />
                        )}
                      </IconButton>
                    </Box>
                    {isIncorrectInitalPageId && (
                      <Alert
                        onClose={() => setIsIncorrectInitialPageId(false)}
                        severity={'error'}
                      >
                        <Typography>
                          {I18n.t(
                            'facebookPermissionsManager.incorrectFacebookPageId',
                          )}
                        </Typography>
                      </Alert>
                    )}
                    <FormControl fullWidth={true}>
                      <Select
                        onChange={handleFacebookPageChange}
                        value={currentPageId}
                      >
                        {facebookPages.map((facebookPage: FacebookPage) => (
                          <MenuItemWithCheckbox
                            key={facebookPage.id}
                            value={facebookPage.id}
                          >
                            <Box alignItems="center" display={'flex'}>
                              <Box pr={3}>
                                <FacebookThumbnail
                                  facebookEntity={facebookPage}
                                  isTokenValid={
                                    facebookPage?.remote_token_status
                                      ?.is_valid || false
                                  }
                                />
                              </Box>
                              <Typography className={classes.name}>
                                {facebookPage.name}
                              </Typography>
                              {facebookPage.graph_id &&
                              manageablePageIds.indexOf(
                                facebookPage.graph_id,
                              ) !== -1 ? null : (
                                <Box ml={2}>
                                  <ErrorOutline color={'error'} />
                                </Box>
                              )}
                            </Box>
                          </MenuItemWithCheckbox>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}
              </Grid>
              <Grid item={true} sm={8} xs={12}>
                {forceNoPagesWarning ? (
                  <Alert
                    action={
                      <Button
                        className={classes.fixIt}
                        color={'secondary'}
                        onClick={fixFacebookAccountPermissions}
                        size={'small'}
                        variant={'contained'}
                      >
                        {I18n.t('facebookPermissionsManager.fixIt')}
                      </Button>
                    }
                    severity={'error'}
                  >
                    <Typography>
                      {I18n.t('facebookPermissionsManager.noMorePages')}
                    </Typography>
                  </Alert>
                ) : undefined}
                {isTokenValid &&
                  showAddSelectPage &&
                  availablePages &&
                  availablePages.length > 0 && (
                    <>
                      <Typography variant={'h6'}>
                        {I18n.t('facebookPermissionsManager.selectAPage')}
                      </Typography>
                      {availablePages && availablePages.length > 0 && (
                        <Box
                          alignItems={'flex-start'}
                          display={'flex'}
                          flexDirection={'column'}
                        >
                          <FormControl fullWidth={true}>
                            {availablePages &&
                              availablePages.map((availablePage, index) => (
                                <ListItem
                                  button
                                  onClick={() =>
                                    handleSelectedAvailablePage(
                                      availablePage?.id,
                                    )
                                  }
                                >
                                  <Box
                                    alignItems="center"
                                    display={'flex'}
                                    key={availablePage?.id}
                                  >
                                    <Checkbox
                                      checked={selectedAvailablePageIds.includes(
                                        availablePage?.id,
                                      )}
                                      onChange={() =>
                                        handleSelectedAvailablePage(
                                          availablePage?.id,
                                        )
                                      }
                                    />
                                    <Box pr={3}>
                                      <img
                                        alt={availablePage?.name || ''}
                                        className={classes.imageThumbnail}
                                        src={`https://graph.facebook.com/${availablePage.id}/picture?type=square`}
                                      />
                                    </Box>
                                    <Typography className={classes.name}>
                                      {availablePage?.name}
                                    </Typography>
                                  </Box>
                                </ListItem>
                              ))}
                          </FormControl>
                          <Box display={'flex'} flexDirection={'row'}>
                            {facebookPages && facebookPages.length > 0 && (
                              <Box pr={2}>
                                <Button
                                  onClick={() => {
                                    setAddPageMode(false)
                                  }}
                                  variant={'contained'}
                                >
                                  {I18n.t('shared.cancel')}
                                </Button>
                              </Box>
                            )}
                            <Button
                              color={'primary'}
                              onClick={() => addFacebookPages()}
                              variant={'contained'}
                              disabled={!selectedAvailablePageIds.length}
                            >
                              <Add />
                              {I18n.t('facebookPermissionsManager.addPage', {
                                count: selectedAvailablePageIds.length,
                              })}
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </>
                  )}
                {!isTokenValid && (
                  <Alert
                    action={
                      <Button
                        className={classes.fixIt}
                        color={'secondary'}
                        onClick={fixFacebookAccountPermissions}
                        size={'small'}
                        variant={'contained'}
                      >
                        {I18n.t('facebookPermissionsManager.fixIt')}
                      </Button>
                    }
                    severity={'error'}
                  >
                    <Typography>
                      {I18n.t('facebookPermissionsManager.invalidAccountToken')}
                    </Typography>
                  </Alert>
                )}
                {isTokenValid &&
                  currentPage &&
                  currentPageId &&
                  !showAddSelectPage &&
                  isFacebookPageTokenValid(currentPage) && (
                    <FacebookPermissionsTable
                      hasUserControl={hasUserControl}
                      deleteFacebookPage={handleDeleteFacebookPage}
                      facebookPage={currentPage}
                      fixFacebookAccountPermissions={
                        fixFacebookAccountPermissions
                      }
                      fixInstagramBusinessAccount={fixInstagramBusinessAccount}
                    />
                  )}
                {isTokenValid &&
                  currentPage &&
                  !showAddSelectPage &&
                  currentPageAccount &&
                  !isFacebookPageTokenValid(currentPage) &&
                  isFacebookAccountTokenValid(currentPageAccount) && (
                    <Alert
                      action={
                        <Button
                          className={classes.fixIt}
                          color={'secondary'}
                          onClick={fixFacebookPageToken}
                          size={'small'}
                          variant={'contained'}
                        >
                          {I18n.t('facebookPermissionsManager.fixIt')}
                        </Button>
                      }
                      severity={'error'}
                    >
                      <Typography>
                        {hasAttemptedFix
                          ? I18n.t('facebookPermissionsManager.fixItFailure')
                          : I18n.t(
                              'facebookPermissionsManager.invalidPageToken',
                            )}
                      </Typography>
                    </Alert>
                  )}
                {isTokenValid &&
                  currentPage &&
                  !showAddSelectPage &&
                  !isFacebookPageTokenValid(currentPage) &&
                  currentPageAccount &&
                  !isFacebookAccountTokenValid(currentPageAccount) && (
                    <Alert
                      action={
                        <Button
                          className={classes.fixIt}
                          color={'secondary'}
                          onClick={fixFacebookAccountPermissions}
                          size={'small'}
                          variant={'contained'}
                        >
                          {I18n.t('facebookPermissionsManager.fixIt')}
                        </Button>
                      }
                      severity={'error'}
                    >
                      <Typography>
                        {I18n.t(
                          'facebookPermissionsManager.invalidAccountToken',
                        )}
                      </Typography>
                    </Alert>
                  )}
                {isTokenValid &&
                  currentPage &&
                  !showAddSelectPage &&
                  !isFacebookPageTokenValid(currentPage) &&
                  !currentPageAccount && (
                    <Tooltip
                      title={I18n.t(
                        'facebookPermissionsManager.mightBeSocialMediaManager',
                      )}
                    >
                      <Alert severity={'error'}>
                        <Typography>
                          {I18n.t(
                            'facebookPermissionsManager.invalidPageTokenNoAccess',
                          )}
                        </Typography>
                      </Alert>
                    </Tooltip>
                  )}
              </Grid>
            </Grid>
          )}
        </Box>
        {currentFacebookAccountId &&
          getFacebookAccountById(currentFacebookAccountId) && (
            <ConfirmDialog
              content={I18n.t(
                'facebookPermissionsManager.removeThisAccountConfirm',
                {
                  accountName:
                    (currentFacebookAccountId &&
                      getFacebookAccountById(currentFacebookAccountId)?.name) ||
                    '',
                },
              )}
              onClose={() => {
                setAnchorEl(null)
                closeModal()
              }}
              onConfirmClick={handleDeleteFacebookAccount}
              open={isOpen}
              title={I18n.t(
                'facebookPermissionsManager.removeThisAccountTitle',
              )}
            />
          )}
        {isWorking && <Overlay />}
      </DialogContent>
      <DialogActions>
        <div className={classes.permissionsExplanation}>
          <FacebookPermissionsExplanation />
        </div>
        <Button onClick={handleDialogCancel} variant={'outlined'}>
          {I18n.t('shared.close')}
        </Button>
        <Button
          color={'primary'}
          disabled={!isValid || isWorking || loading}
          onClick={handleDialogDone}
          variant={'contained'}
        >
          {I18n.t('shared.done')}
        </Button>
      </DialogActions>
    </>
  )
}
